import { useEffect, useState } from 'react';
import { BrandLink } from '../../_Shared/BrandLink';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useScrollLock } from '../../../_Hooks/useScrollLock';

const BLOCK = 'LStudio__app_sidebar';

/*
    🔥 SIDEBAR PRIVADO

    Acá vive la MARCA y la navegación privada.
    El header privado queda reservado solo para los iconos.

    Escritorio -> columna fija a la izquierda
    Mobile     -> cajón con backdrop, lo abre el botón del header

    ⚠️ AHORA SOPORTA SUBITEMS

    Un item con children se dibuja como grupo plegable. Sin children se
    comporta EXACTAMENTE como antes: un enlace suelto. Nada de lo que
    ya funcionaba cambia.

        { path: '/dashboard', label: 'Dashboard' }
        { path: '/templates', label: 'Templates', children: [
            { path: '/templates/button', label: 'Botón' },
            …
        ]}
*/

/*
    🔥 ¿ESTE ITEM ES EL ACTIVO?

    ⚠️ NO ALCANZA CON startsWith

    Con startsWith, estando en /blog-admin el item /blog también se
    marca activo, porque "/blog-admin".startsWith("/blog") es true.
    Hay que exigir que después del prefijo venga una barra o el final
    de la dirección.
*/
const onIsActive = (pathname, path) => {
    if (path === '/dashboard') return pathname === path;
    return pathname === path || pathname.startsWith(`${path}/`);
};

// ---------------------------------------- 🔥 GRUPO PLEGABLE
const SidebarGroup = ({ item, pathname, onClose }) => {

    const IS_INSIDE = onIsActive(pathname, item.path);

    /*
        ⚠️ ARRANCA ABIERTO SI ESTÁS ADENTRO

        Si no, entrás por un enlace directo a /templates/button y el
        grupo aparece cerrado: no ves dónde estás parado ni cómo
        llegaste.
    */
    const [useIsOpen, setIsOpen] = useState(IS_INSIDE);

    // 🔥 Al navegar hacia adentro del grupo, se abre solo
    useEffect(() => { if (IS_INSIDE) setIsOpen(true); }, [IS_INSIDE]);

    return (
        <div className={`${BLOCK}__group`} data-open={useIsOpen} data-inside={IS_INSIDE}>

            <button
                type="button"
                className={`${BLOCK}__link ${BLOCK}__group_head`}
                data-active={IS_INSIDE}
                aria-expanded={useIsOpen}
                onClick={() => setIsOpen(!useIsOpen)}
            >

                <span className={`${BLOCK}__group_label`}>{item.label}</span>

                <span className={`${BLOCK}__group_count`}>{item.children.length}</span>

                <FaChevronDown className={`${BLOCK}__group_caret`} aria-hidden="true" />

            </button>

            {
                useIsOpen && (
                    <div className={`${BLOCK}__sub`}>
                        {
                            item.children.map((child) => (
                                <Link
                                    key={child.path}
                                    to={child.path}
                                    onClick={onClose ?? undefined}
                                    className={`${BLOCK}__sublink`}
                                    /*
                                        ⚠️ ACÁ SÍ SE COMPARA EXACTO

                                        Los subitems son hojas: /templates/button
                                        no contiene a nadie. Con startsWith,
                                        /templates/table marcaría también a
                                        /templates/tableX si algún día existiera.
                                    */
                                    data-active={pathname === child.path}
                                >
                                    {child.label}
                                </Link>
                            ))
                        }
                    </div>
                )
            }

        </div>
    );
};

export const SidebarPrivate = ({
    brand,
    navItems = [],
    groupLabel = 'Panel',
    homePath = '/dashboard',
    isOpen = false,
    onClose = null,
}) => {

    const SLOT = brand?.headerPrivate;

    const { pathname } = useLocation();

    /*
        🔥 BLOQUEO DEL SCROLL CON EL CAJÓN ABIERTO

        ⚠️ ANTES ESTO NO ALCANZABA EN MÓVIL

        Este componente ponía overflow: hidden en el body, y en Safari
        de iPhone eso NO frena el scroll: el gesto sigue moviendo la
        página detrás del cajón. Además la barra de scroll seguía
        visible al costado.

        El hook compartido fija el body en su posición actual, esconde
        la barra y devuelve todo al cerrar. Y lleva un contador: si
        además hay un modal abierto, el scroll se libera recién cuando
        se cierran los dos.
    */
    useScrollLock(isOpen);

    // 🔥 Cierra con Escape
    useEffect(() => {

        if (!onClose) return;

        const onEscape = (event) => { if (event.key === 'Escape') onClose(); };

        document.addEventListener('keydown', onEscape);
        return () => document.removeEventListener('keydown', onEscape);

    }, [onClose]);

    return (
        <>
            {isOpen && <div className={`${BLOCK}__backdrop`} onClick={onClose} aria-hidden="true" />}

            <aside className={BLOCK} data-open={isOpen}>

                {/*
                    🔥 ENVOLTORIO PEGAJOSO

                    ⚠️ POR QUÉ HACE FALTA ESTA CAPA

                    Antes el <aside> era el que llevaba position: sticky y
                    height: 100dvh. Eso lo convierte en una caja de alto
                    fijo: si el contenido de la derecha es más largo que la
                    ventana, el sidebar TERMINA a los 100dvh y debajo se ve
                    el fondo del shell. Un hueco de color distinto que
                    aparece apenas scrolleás.

                    Ahora el <aside> se estira a todo el alto de la fila
                    —así el fondo cubre siempre— y es esta capa de adentro
                    la que se queda pegada arriba con el alto de la ventana.

                    Es el patrón clásico de sidebar pegajoso: el fondo lo
                    pone el contenedor, el pegado lo hace el contenido.
                */}
                <div className={`${BLOCK}__sticky`}>

                <div className={`${BLOCK}__head`}>

                    <BrandLink slot={SLOT} homePath={homePath} className={`${BLOCK}__brand`} isLogo={SLOT?.isLogo === true}>
                        {SLOT?.isLogo && SLOT?.logoLight && (
                                <img src={SLOT.logoLight} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--light`}
                                    style={SLOT?.imageStyle}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {SLOT?.isLogo && SLOT?.logoDark && (
                                <img src={SLOT.logoDark} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--dark`}
                                    style={SLOT?.imageStyle}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {!SLOT?.isLogo && <span className={`${BLOCK}__brand_name`}>{brand?.name}</span>}

                    </BrandLink>

                    {
                        onClose && (
                            <button type="button" className={`${BLOCK}__close`}
                                aria-label="Cerrar menú" onClick={onClose} >
                                <FaTimes />
                            </button>
                        )
                    }

                </div>

                <nav className={`${BLOCK}__nav`} aria-label="Panel">

                    {groupLabel && <p className={`${BLOCK}__title`}>{groupLabel}</p>}

                    {
                        navItems.map((item) => (

                            (item.children ?? []).length > 0

                                // 🔥 Con hijos: grupo plegable
                                ? (
                                    <SidebarGroup
                                        key={item.path}
                                        item={item}
                                        pathname={pathname}
                                        onClose={onClose}
                                    />
                                )

                                // 🔥 Sin hijos: exactamente como antes
                                : (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={onClose ?? undefined}
                                        className={`${BLOCK}__link ${onIsActive(pathname, item.path) ? `${BLOCK}__link--active` : ''}`}
                                    >
                                        {item.label}
                                    </Link>
                                )
                        ))
                    }

                </nav>

                </div>

            </aside>
        </>
    );
};
